import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Search as SearchIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SequenceCard } from '@/components/sequences/SequenceCard';
import { SequenceFiltersSidebar } from '@/components/sequences/SequenceFiltersSidebar';
import { useSequenceFilter } from '@/hooks/useSequenceFilter';
import { Sequence, SoftwareType, HolidayType } from '@/types/sequence';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Use the proper sequence filter hook for real Supabase data
  const { sequences, isLoading, filters } = useSequenceFilter();
  
  // Get search parameters
  const keyword = searchParams.get('keyword') || '';
  
  // State
  const [searchQuery, setSearchQuery] = useState(keyword);
  const [selectedSoftware, setSelectedSoftware] = useState<SoftwareType[]>([]);
  const [selectedHolidays, setSelectedHolidays] = useState<HolidayType[]>([]);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedArtists, setSelectedArtists] = useState<string[]>([]);
  const [selectedAlbums, setSelectedAlbums] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 100]);

  // Handle search form submission
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const params = new URLSearchParams(searchParams);
      params.set('keyword', searchQuery.trim());
      setSearchParams(params);
    }
  };

  // The sequences are now filtered automatically by the useSequenceFilter hook
  // based on URL parameters

  // Handler functions for filters
  const handleSoftwareChange = (software: SoftwareType) => {
    setSelectedSoftware(prev => 
      prev.includes(software) ? prev.filter(s => s !== software) : [...prev, software]
    );
  };

  const handleHolidayChange = (holiday: HolidayType) => {
    setSelectedHolidays(prev =>
      prev.includes(holiday) ? prev.filter(h => h !== holiday) : [...prev, holiday]
    );
  };

  const handleGenreChange = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]
    );
  };

  const clearFilters = () => {
    setSelectedSoftware([]);
    setSelectedHolidays([]);
    setSelectedGenres([]);
    setSelectedArtists([]);
    setSelectedAlbums([]);
    setPriceRange([0, 100]);
    setSearchQuery('');
    navigate('/search');
  };

  return (
    <>
      <Helmet>
        <title>{`Search Results${keyword ? ` for "${keyword}"` : ''} | Light Hunt`}</title>
        <meta name="description" content={`Search results for light show sequences${keyword ? ` matching "${keyword}"` : ''}`} />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />
        
        <main className="flex-1">
          {/* Search Bar */}
          <div className="sticky top-16 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
            <div className="container py-4">
              <form onSubmit={handleSearch} className="mx-auto max-w-2xl">
                <div className="relative flex items-center gap-2">
                  <div className="relative flex-1">
                    <SearchIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder="Search songs, props, genres or artists..."
                      className="pl-10"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <Button type="submit" className="shrink-0">
                    Search
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Content Area with Extra Padding */}
          <div className="pt-8">
            {/* Search Results */}
            <div className="container py-8">
              <h1 className="text-2xl font-semibold mb-6">
                {keyword 
                  ? `Search Results for "${keyword}"`
                  : 'All Sequences'
                }
                {sequences.length > 0 && (
                  <span className="text-muted-foreground ml-2 text-lg">
                    ({sequences.length} {sequences.length === 1 ? 'result' : 'results'})
                  </span>
                )}
              </h1>

              <div className="flex gap-8">
                {/* Filters Sidebar */}
                <aside className="hidden md:block w-64 sticky top-32">
                  <SequenceFiltersSidebar
                    selectedSoftware={selectedSoftware}
                    selectedHolidays={selectedHolidays}
                    selectedGenres={selectedGenres}
                    selectedArtists={selectedArtists}
                    selectedAlbums={selectedAlbums}
                    priceRange={priceRange}
                    allGenres={[]}
                    artists={[]}
                    albums={[]}
                    onSoftwareChange={handleSoftwareChange}
                    onHolidayChange={handleHolidayChange}
                    onGenreChange={handleGenreChange}
                    onArtistChange={() => {}}
                    onAlbumChange={() => {}}
                    onPriceChange={() => {}}
                    clearFilters={clearFilters}
                  />
                </aside>

                {/* Results Grid */}
                <div className="flex-1">
                  {isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {[...Array(6)].map((_, i) => (
                        <div
                          key={i}
                          className="aspect-square bg-muted animate-pulse rounded-lg"
                        />
                      ))}
                    </div>
                  ) : sequences.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {sequences.map((sequence) => (
                        <SequenceCard key={sequence.id} sequence={sequence} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <h2 className="text-xl font-semibold mb-2">No sequences found</h2>
                      <p className="text-muted-foreground">
                        Try adjusting your search terms or filters
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
} 